import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackBeginCheckout, trackSelectSize } from "@/lib/analytics";
import { SizeGuide } from "@/components/SizeGuide";

export const BuyBox = ({ product, large = false, initialSize }) => {
  const sizes = product.sizes || [];
  const preset = initialSize && sizes.includes(initialSize) ? initialSize : sizes.length === 1 ? sizes[0] : "";
  const [size, setSize] = useState(preset);
  const [loading, setLoading] = useState(false);

  const pickSize = (s) => {
    setSize(s);
    trackSelectSize(product, s);
  };

  const buy = async () => {
    if (!size) {
      toast.error("Pick a size first.");
      return;
    }
    setLoading(true);
    trackBeginCheckout(product, size, 1);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: product.slug, size, quantity: 1, origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch {
      toast.error("Checkout failed. Try again.");
      setLoading(false);
    }
  };

  return (
    <div className="mt-4" data-testid={`buy-box-${product.slug}`}>
      {sizes.length > 1 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {sizes.map((s) => (
            <button
              key={s}
              data-testid={`size-${s.toLowerCase().replace("/", "-")}-${product.slug}`}
              onClick={() => pickSize(s)}
              className={`font-mono-label border transition-colors duration-200 ${large ? "px-5 py-3.5" : "px-3.5 py-3"} ${
                size === s ? "bg-white text-ink border-white" : "border-white/25 text-white/60 hover:border-white"
              }`}
            >
              {s}
            </button>
          ))}
          {large && <SizeGuide product={product} />}
        </div>
      )}
      {sizes.length === 1 && <p className="font-mono-label text-white/40">{sizes[0]}</p>}
      <button
        data-testid={`buy-button-${product.slug}`}
        onClick={buy}
        disabled={loading}
        className={`btn-invert font-mono-label bg-white text-ink border border-white w-full hover:bg-transparent hover:text-white transition-colors duration-200 disabled:opacity-50 ${large ? "px-6 py-4 mt-4" : "px-4 py-3 mt-3"}`}
      >
        {loading ? "OPENING CHECKOUT..." : `BUY NOW · $${product.price}`}
      </button>
    </div>
  );
};

export const DropNotify = ({ product }) => {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const res = await api.post("/products/notify", { email, product_slug: product.slug });
      toast.success(res.data.message || "You are on the list.");
      setDone(true);
    } catch {
      toast.error("Something broke. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <p data-testid={`notify-success-${product.slug}`} className="font-mono-label text-white/80 border border-white/25 px-4 py-3 mt-4">
        ✓ YOU'RE ON THE LIST. FIRST TO KNOW.
      </p>
    );
  }
  if (!open) {
    return (
      <button
        data-testid={`notify-button-${product.slug}`}
        onClick={() => setOpen(true)}
        className="btn-invert font-mono-label border border-white/40 px-4 py-3 mt-4 w-full hover:bg-white hover:text-ink transition-colors duration-200"
      >
        NOTIFY ME WHEN IT DROPS
      </button>
    );
  }
  return (
    <form onSubmit={submit} className="flex gap-2 mt-4" data-testid={`notify-form-${product.slug}`}>
      <input
        data-testid={`notify-email-${product.slug}`}
        type="email"
        required
        autoFocus
        placeholder="EMAIL ADDRESS"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        className="flex-1 min-w-0 bg-transparent border-b border-white/30 py-2 text-sm placeholder:text-white/30 focus:border-white transition-colors duration-200"
      />
      <button
        type="submit"
        disabled={loading}
        data-testid={`notify-submit-${product.slug}`}
        className="btn-invert font-mono-label bg-white text-ink border border-white px-4 py-2 hover:bg-transparent hover:text-white disabled:opacity-50 shrink-0"
      >
        {loading ? "..." : "NOTIFY ME"}
      </button>
    </form>
  );
};
